import React, { useRef } from 'react';
import { Star, MessageCircle, ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { counsellors as fallbackCounsellors } from '../data/mock';
import { useContent } from '../hooks/useContent';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

const gradients = [
  'from-blue-500 to-blue-800',
  'from-teal-500 to-teal-800',
  'from-orange-500 to-orange-800',
  'from-purple-500 to-purple-800',
  'from-rose-500 to-rose-800',
  'from-emerald-500 to-emerald-800',
];

export default function Counsellors() {
  const counsellors = useContent('counsellors', fallbackCounsellors) || fallbackCounsellors;
  const ref = useRef(null);
  const scroll = (dir) => { if (ref.current) ref.current.scrollBy({ left: dir === 'left' ? -288 : 288, behavior: 'smooth' }); };

  return (
    <section id="counsellors" className="py-20 bg-muted">
      <div className="container mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <h2 className="text-sm font-bold text-secondary uppercase tracking-widest mb-2">Expert Counsellors</h2>
            <h3 className="font-serif text-4xl font-bold text-primary mb-3">Honest Guidance, Not a Sales Pitch.</h3>
            <p className="text-muted-foreground max-w-xl">6 verified MBBS counsellors — salaried, not commission-based. They help you choose right, not just fast.</p>
          </div>
          <Link to="/video-counselling">
            <Button className="bg-primary text-white rounded-full px-6 gap-2 hover:bg-primary/90 shrink-0 btn-shift">
              Talk with All 6 Counsellors →
            </Button>
          </Link>
        </div>

        <div className="relative">
          <button onClick={() => scroll('left')} aria-label="Scroll left"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll('right')} aria-label="Scroll right"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 h-9 w-9 rounded-full bg-white border border-border shadow-md flex items-center justify-center hover:bg-primary hover:text-white hover:border-primary transition-all">
            <ChevronRight className="h-4 w-4" />
          </button>

          <div ref={ref} className="flex gap-5 overflow-x-auto pb-4 -mx-4 px-4 md:-mx-8 md:px-8 snap-x snap-mandatory scrollbar-hide">
            {counsellors.map((cl, i) => (
              <div key={i} className="shrink-0 w-64 snap-start rounded-2xl overflow-hidden bg-white shadow-md border border-border hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1">
                <div className={`relative h-52 bg-gradient-to-br ${gradients[i % gradients.length]} flex items-end`}>
                  <div className="absolute top-3 left-3 flex items-center gap-1 bg-white/95 rounded-full px-2.5 py-1 shadow text-xs font-bold text-yellow-600">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" strokeWidth={0} /> {cl.rating}
                  </div>
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-white rounded-full px-2.5 py-1 shadow text-xs font-semibold text-[#1877F2]">
                    <BadgeCheck className="h-3 w-3 text-white" fill="#1877F2" strokeWidth={0} /> Verified
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {cl.photo ? (
                      <img
                        src={resolveImg(cl.photo)}
                        alt={cl.name}
                        className="h-32 w-32 rounded-full object-cover border-4 border-white/40 shadow-lg"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-white text-2xl font-bold shadow-lg backdrop-blur-sm">
                        {cl.initials}
                      </div>
                    )}
                  </div>
                  <div className="relative w-full bg-gradient-to-t from-black/60 to-transparent px-4 pb-3 pt-8">
                    <p className="text-white font-bold text-sm leading-tight">{cl.name}</p>
                    <p className="text-white/80 text-xs">{cl.years} Years experience</p>
                  </div>
                </div>
                <div className="p-4 flex flex-col gap-3">
                  <div className="flex justify-between text-center">
                    <div>
                      <p className="text-primary font-bold text-sm">{cl.students}</p>
                      <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Students</p>
                    </div>
                    <div>
                      <p className="text-primary font-bold text-sm">{cl.reviews}</p>
                      <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Reviews</p>
                    </div>
                    <div>
                      <p className="text-primary font-bold text-sm">📍 {cl.city}</p>
                      <p className="text-muted-foreground text-[10px] uppercase tracking-wide">{cl.state}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {cl.tags.map((t) => (
                      <span key={t} className="bg-primary/10 text-primary text-[10px] font-semibold px-2 py-0.5 rounded-full">✦ {t}</span>
                    ))}
                  </div>
                  <p className="text-xs text-primary font-semibold text-center pt-1 flex items-center justify-center gap-1">
                    <MessageCircle className="h-3.5 w-3.5" /> Click to view profile &amp; contact
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">Free · No registration required · Sessions within 24 hrs</p>
      </div>
    </section>
  );
}
